import { useCallback, useRef, useState } from 'react';
import {
  formatLocationDisplay,
  placeResultToLocation,
  reverseGeocode,
} from '@/lib/google-maps';
import type { MapLocation } from './types';
import { createClearedLocation } from './utils';

interface UseMapLocationOptions {
  geocodeOnPlace: boolean;
  showSearch: boolean;
  onLocationChange?: (location: MapLocation, displayName?: string) => void;
  onLocationNameChange?: (name: string) => void;
  captureMapViewport: () => void;
  setShowMapMarker: (show: boolean) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function useMapLocation({
  geocodeOnPlace,
  showSearch,
  onLocationChange,
  onLocationNameChange,
  captureMapViewport,
  setShowMapMarker,
  inputRef,
}: UseMapLocationOptions) {
  const locationRef = useRef<MapLocation | null | undefined>(undefined);
  const geocodeRequestRef = useRef(0);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const setLocationRef = useCallback(
    (location: MapLocation | null | undefined) => {
      locationRef.current = location;
    },
    [],
  );

  const setLocationDisplay = useCallback(
    (displayName: string) => {
      if (inputRef.current) {
        inputRef.current.value = displayName;
      }
      onLocationNameChange?.(displayName);
    },
    [inputRef, onLocationNameChange],
  );

  const applyLocation = useCallback(
    (nextLocation: MapLocation, displayName?: string) => {
      locationRef.current = nextLocation;
      if (displayName) {
        setLocationDisplay(displayName);
        onLocationChange?.(nextLocation, displayName);
        return;
      }
      onLocationChange?.(nextLocation);
    },
    [onLocationChange, setLocationDisplay],
  );

  const clearLocation = useCallback(() => {
    geocodeRequestRef.current += 1;
    setIsGeocoding(false);
    setShowMapMarker(false);
    captureMapViewport();

    const clearedLocation = createClearedLocation(locationRef.current);
    locationRef.current = clearedLocation;

    if (showSearch || onLocationNameChange) {
      setLocationDisplay('');
    }
    onLocationChange?.(clearedLocation);
  }, [
    captureMapViewport,
    onLocationChange,
    onLocationNameChange,
    setLocationDisplay,
    setShowMapMarker,
    showSearch,
  ]);

  const resolveCoordinates = useCallback(
    async (lat: number, lng: number) => {
      const baseLocation: MapLocation = {
        ...(locationRef.current ?? {}),
        latitude: lat,
        longitude: lng,
      };

      if (!geocodeOnPlace) {
        locationRef.current = baseLocation;
        onLocationChange?.(baseLocation);
        return;
      }

      const requestId = geocodeRequestRef.current + 1;
      geocodeRequestRef.current = requestId;
      setIsGeocoding(true);
      locationRef.current = { latitude: lat, longitude: lng };
      onLocationChange?.({ latitude: lat, longitude: lng });

      try {
        const resolved = await reverseGeocode(lat, lng);
        if (geocodeRequestRef.current !== requestId) {
          return;
        }
        const displayName = formatLocationDisplay(resolved);
        applyLocation(resolved, displayName);
      } catch {
        if (geocodeRequestRef.current === requestId) {
          onLocationChange?.({ latitude: lat, longitude: lng });
        }
      } finally {
        if (geocodeRequestRef.current === requestId) {
          setIsGeocoding(false);
        }
      }
    },
    [applyLocation, geocodeOnPlace, onLocationChange],
  );

  const handlePlaceChanged = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      const place = autocomplete.getPlace();
      if (!place) {
        return;
      }

      const nextLocation = placeResultToLocation(place);
      if (!nextLocation) {
        return;
      }

      const displayName =
        formatLocationDisplay(nextLocation) ||
        place.formatted_address ||
        place.name ||
        '';
      applyLocation(nextLocation, displayName);
    },
    [applyLocation],
  );

  return {
    isGeocoding,
    clearLocation,
    resolveCoordinates,
    applyLocation,
    setLocationDisplay,
    setLocationRef,
    handlePlaceChanged,
  };
}
